import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { parsePgm } from "../pages/mapGrid";
import {
  PLAN_MAX_EDGE_PX, frameDiagonalM, worldToPixel, type MapFrame2d,
} from "../pages/planRaster";
import { decodeThemedPlan } from "../pages/planDecode";
import { authHeaders, getWarehousePlan, listZones } from "../api/rest";
import { drawZonesPlan, drawablePolygons, zonesPlanFrame } from "../pages/zonesPlan";
import { readViewRays, viewFootprint } from "../scene/viewFootprint";
import { useTelemetryStore, getDisplayStatus, gatewayOnlineForAsset } from "../stores/telemetryStore";
import { useSiteStore } from "../stores/siteStore";
import { tokens } from "../styles/tokens";
import css from "./Minimap.module.css";

/**
 * Themed 2D floor plan (ADR 103): the warehouse's raster recolored to the design
 * tokens, with live markers and the current 3D view region on top. The frame meta
 * gives the pose→pixel mapping, so markers land exactly where the camera says
 * they are.
 *
 * Two sources, in precedence order (ADR 215):
 *   1. an uploaded 2D plan (`/v1/sites/…/warehouses/{id}/plan`) — a real floor
 *      plan, available without a V2Pro walk, and the operator's own drawing wins
 *      over a machine's scan of the same building;
 *   2. the archived SLAM occupancy raster (`/v1/models/warehouses/{id}/map.pgm`).
 */

interface Source { frame: MapFrame2d; image: ImageData }

function parseFrame(yaml: string): { originX: number; originY: number; resolution: number } {
  const res = /resolution:\s*([0-9.eE+-]+)/.exec(yaml);
  const org = /origin:\s*\[\s*([0-9.eE+-]+)\s*,\s*([0-9.eE+-]+)/.exec(yaml);
  return {
    resolution: res ? parseFloat(res[1]) : 0.05,
    originX: org ? parseFloat(org[1]) : 0,
    originY: org ? parseFloat(org[2]) : 0,
  };
}

const STATUS_COLOR: Record<string, string> = {
  loaded:   tokens.semantic.success,
  empty:    tokens.blue[300],
  idle:     tokens.semantic.warn,
  offline:  tokens.ink[500],
  linkdown: tokens.semantic.danger,
};

// ROS map_server raster: 0 = occupied, 205 = unknown, ~254 = free.
function themeRaster(w: number, h: number, data: Uint8Array): ImageData {
  const img = new ImageData(w, h);
  const wall = { r: 0xa6, g: 0xc2, b: 0xf7 };  // blue-300
  const free = { r: 0x1e, g: 0x5b, b: 0xd8 };  // blue-500 @ low alpha
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    const o = i * 4;
    if (v === 205) continue;                    // unknown → transparent
    if (v < 89) {                               // occupied_thresh 0.65 equivalent
      img.data[o] = wall.r; img.data[o + 1] = wall.g; img.data[o + 2] = wall.b;
      img.data[o + 3] = 235;
    } else {
      img.data[o] = free.r; img.data[o + 1] = free.g; img.data[o + 2] = free.b;
      img.data[o + 3] = 46;
    }
  }
  return img;
}

/**
 * Last resort (ADR 303): the warehouse's own zone footprints, rasterised. A zones-built
 * warehouse was never driven, so it has no SLAM map — and four of six sites had no 2D
 * plan at all. A schematic of where the aisles are beats nothing to orient against.
 */
async function loadZonesPlan(siteId: string, warehouseId: string): Promise<Source | null> {
  const polygons = drawablePolygons(await listZones(siteId, warehouseId));
  if (polygons.length === 0) return null;
  const frame = zonesPlanFrame(polygons, PLAN_MAX_EDGE_PX);
  if (!frame) return null;
  const image = drawZonesPlan(frame, polygons);
  return image ? { frame, image } : null;
}

/** The uploaded plan, decoded, downscaled and recolored (ADR 215). */
async function loadPlan(siteId: string, warehouseId: string): Promise<Source | null> {
  const plan = await getWarehousePlan(siteId, warehouseId);
  if (!plan) return null;
  if (typeof createImageBitmap !== "function") return null;
  // authHeaders: /v1/models/** is gateway-secured like any other route (ADR 204),
  // and an <img src> cannot carry a bearer token — so fetch the bytes ourselves.
  const res = await fetch(plan.url, { headers: authHeaders(), credentials: "include" });
  if (!res.ok) return null;
  const decoded = await decodeThemedPlan(await res.blob(), PLAN_MAX_EDGE_PX);
  if (!decoded) return null;
  return {
    image: decoded.image,
    frame: {
      widthPx: decoded.width,
      heightPx: decoded.height,
      // The stored resolution is m per ORIGINAL pixel; downscaling for display
      // scales it, or every marker lands off by the decode ratio.
      resolution: plan.resolution * (decoded.sourceWidth / decoded.width),
      originX: plan.originX,
      originY: plan.originY,
    },
  };
}

/** The archived SLAM occupancy raster + its yaml frame. */
async function loadSlamRaster(warehouseId: string): Promise<Source | null> {
  const bust = `?v=${Date.now()}`;
  const [pgmRes, yamlRes] = await Promise.all([
    fetch(`/v1/models/warehouses/${warehouseId}/map.pgm${bust}`, { headers: authHeaders(), credentials: "include" }),
    fetch(`/v1/models/warehouses/${warehouseId}/map.yaml${bust}`, { headers: authHeaders(), credentials: "include" }),
  ]);
  if (!pgmRes.ok || !yamlRes.ok) return null;   // no archived raster: stay hidden
  const pgm = parsePgm(new Uint8Array(await pgmRes.arrayBuffer()));
  const meta = parseFrame(await yamlRes.text());
  // The yaml origin is ROS map-frame: the BOTTOM-left pixel, y-up. The twin frame
  // negates map y (ADR 189), which turns the pgm's top row into the minimum twin y
  // — exactly the image-like frame worldToPixel reads (ADR 311). Convert the
  // corner: topLeft.y(twin) = −(origin.y(map) + height·res). Feeding the raw yaml
  // value in mixed frames is what stranded markers off the raster before.
  return {
    image: themeRaster(pgm.width, pgm.height, pgm.data),
    frame: {
      widthPx: pgm.width,
      heightPx: pgm.height,
      resolution: meta.resolution,
      originX: meta.originX,
      originY: -(meta.originY + pgm.height * meta.resolution),
    },
  };
}

export function Minimap() {
  const { t } = useTranslation();
  const siteId = useSiteStore((s) => s.activeSiteId);
  const warehouseId = useSiteStore((s) => s.activeWarehouseId);
  const baseRef = useRef<HTMLCanvasElement | null>(null);
  const viewRef = useRef<HTMLCanvasElement | null>(null);
  const markRef = useRef<HTMLCanvasElement | null>(null);
  const frameRef = useRef<MapFrame2d | null>(null);
  const imageRef = useRef<ImageData | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  // Load the best available raster once per warehouse.
  useEffect(() => {
    if (!warehouseId) return;
    let cancelled = false;
    (async () => {
      let source: Source | null = null;
      try {
        source = await loadPlan(siteId, warehouseId);
      } catch {
        /* unreadable plan: fall through to the SLAM raster */
      }
      if (cancelled) return;
      if (!source) {
        try {
          source = await loadSlamRaster(warehouseId);
        } catch {
          /* offline dev: silently no minimap */
        }
      }
      if (!source) {
        try {
          source = await loadZonesPlan(siteId, warehouseId);
        } catch {
          /* no zones either: no minimap, and the toggle is gated off anyway */
        }
      }
      if (cancelled || !source) return;
      frameRef.current = source.frame;
      imageRef.current = source.image;
      setSize({ w: source.frame.widthPx, h: source.frame.heightPx });
    })();
    return () => { cancelled = true; };
  }, [siteId, warehouseId]);

  // Paint the themed base once the canvases exist at the raster's size.
  useEffect(() => {
    const image = imageRef.current;
    const base = baseRef.current;
    if (!image || !base || !size) return;
    base.getContext("2d")?.putImageData(image, 0, 0);
  }, [size]);

  // ADR 216 — the 3D view's floor footprint. Repainted only when the published
  // pose changed, so an idle camera does no canvas work at all.
  useEffect(() => {
    if (!size) return;
    let raf = 0;
    let drawnVersion = -1;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const rays = readViewRays();
      const frame = frameRef.current;
      const canvas = viewRef.current;
      if (!rays || !frame || !canvas || rays.version === drawnVersion) return;
      drawnVersion = rays.version;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Clamp at the map's diagonal: a near-level camera's far corners run to the
      // horizon, and an indicator that leaves the map tells the operator nothing.
      const quad = viewFootprint(rays.origin, rays.dirs, frameDiagonalM(frame));
      ctx.beginPath();
      quad.forEach(([wx, wy], i) => {
        const [px, py] = worldToPixel(frame, wx, wy);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      });
      ctx.closePath();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = tokens.blue[500];
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = tokens.blue[300];
      ctx.lineWidth = Math.max(1.5, size.w / 220);
      ctx.stroke();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [size]);

  // Markers at 2 Hz — imperative store read so the canvas doesn't re-render React.
  useEffect(() => {
    if (!size) return;
    const draw = () => {
      const canvas = markRef.current;
      const frame = frameRef.current;
      if (!canvas || !frame) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { forklifts, gateways } = useTelemetryStore.getState();
      for (const p of Object.values(forklifts)) {
        const status = getDisplayStatus(p);
        if (status === "offline" && !gatewayOnlineForAsset(gateways, p.id)) continue;
        const [px, py] = worldToPixel(frame, p.x, p.y);
        const r = Math.max(3, size.w / 120);
        ctx.beginPath();
        ctx.arc(px, py, r, 0, Math.PI * 2);
        ctx.fillStyle = STATUS_COLOR[status] ?? tokens.blue[300];
        ctx.fill();
        // Heading tick.
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px + Math.cos(p.yaw) * r * 2.2, py - Math.sin(p.yaw) * r * 2.2);
        ctx.strokeStyle = STATUS_COLOR[status] ?? tokens.blue[300];
        ctx.lineWidth = Math.max(1.5, r / 2);
        ctx.stroke();
      }
    };
    draw();
    const timer = setInterval(draw, 500);
    return () => clearInterval(timer);
  }, [size]);

  if (!size) return null;

  return (
    // ADR 359: the wrap is a fixed tile; .fit shrink-wraps the letterboxed base
    // canvas so the overlays keep 1:1 registration in raster pixels.
    <div className={css.wrap} aria-label={t("layers.minimap")}>
      <div className={css.fit}>
        <canvas ref={baseRef} width={size.w} height={size.h} className={css.base} />
        {/* Under the markers: the view region is context, the fleet is the subject. */}
        <canvas ref={viewRef} width={size.w} height={size.h} className={css.overlay} />
        <canvas ref={markRef} width={size.w} height={size.h} className={css.overlay} />
      </div>
    </div>
  );
}
